import type { Fixture } from '../config/booth';
import type { Point } from './types';

export interface Box {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
}

/** Axis-aligned footprint, padded so bodies keep a little clearance. */
export function fixtureBox(f: Fixture, pad = 0): Box {
  const swap = Math.abs(Math.sin(f.rot)) > 0.5;
  const w = (swap ? f.d : f.w) / 2 + pad;
  const d = (swap ? f.w : f.d) / 2 + pad;
  return { x0: f.x - w, x1: f.x + w, z0: f.z - d, z1: f.z + d };
}

export function inBox(p: Point, b: Box): boolean {
  return p.x >= b.x0 && p.x <= b.x1 && p.z >= b.z0 && p.z <= b.z1;
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

/**
 * Moves `from` toward `to` by at most `step`, sliding around any box it would
 * enter. Returns the new point and whether the target was reached.
 */
export function stepToward(
  from: Point,
  to: Point,
  step: number,
  obstacles: Box[],
): { p: Point; arrived: boolean } {
  const dist = distance(from, to);
  if (dist <= step) {
    const landing = obstacles.some((b) => inBox(to, b)) ? from : to;
    return { p: landing, arrived: landing === to };
  }
  const ux = (to.x - from.x) / dist;
  const uz = (to.z - from.z) / dist;
  const straight = { x: from.x + ux * step, z: from.z + uz * step };
  const blocker = obstacles.find((b) => inBox(straight, b));
  if (!blocker) return { p: straight, arrived: false };
  // Slide along the face we hit, toward whichever corner is nearer the target.
  // Picking the corner from the target (not the heading) keeps the slide
  // consistent frame to frame, so nobody dithers at a counter's edge.
  const hitXFace = from.x < blocker.x0 || from.x > blocker.x1;
  const slide: Point = hitXFace
    ? {
        x: from.x,
        z: from.z + (to.z >= (blocker.z0 + blocker.z1) / 2 ? step : -step),
      }
    : {
        x: from.x + (to.x >= (blocker.x0 + blocker.x1) / 2 ? step : -step),
        z: from.z,
      };
  if (!obstacles.some((b) => inBox(slide, b)))
    return { p: slide, arrived: false };
  const other: Point = hitXFace
    ? { x: from.x, z: 2 * from.z - slide.z }
    : { x: 2 * from.x - slide.x, z: from.z };
  if (!obstacles.some((b) => inBox(other, b)))
    return { p: other, arrived: false };
  return { p: from, arrived: false };
}

/** Moves a point that ended up inside a box to its nearest edge. */
export function pushOut(p: Point, b: Box): Point {
  if (!inBox(p, b)) return p;
  const exits = [
    { d: p.x - b.x0, p: { x: b.x0, z: p.z } },
    { d: b.x1 - p.x, p: { x: b.x1, z: p.z } },
    { d: p.z - b.z0, p: { x: p.x, z: b.z0 } },
    { d: b.z1 - p.z, p: { x: p.x, z: b.z1 } },
  ];
  return exits.reduce((best, e) => (e.d < best.d ? e : best)).p;
}
